import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useGetQualifications } from "@src/appV2/Admin/Qualifications/api/useGetQualifications";
import { useGetWorkplaceTypes } from "@src/appV2/Admin/WorkplaceTypes/api/useGetWorkplaceTypes";
import { Text, type UseModalState } from "@src/lib/ui-react";
import { useState } from "react";

import { MappingFailedDialog } from "./components/MappingFailedDialog";
import { CATEGORY_CODE_DISPLAY_NAMES, DEFAULT_QUALIFICATIONS } from "./constants";
import { useEnableTen99PolicyCoverage } from "./hooks/useEnableTen99PolicyCoverage";

interface EnableTen99PolicyDialogProps {
  modalState: UseModalState;
  workplaceId: string;
  suggestedCategoryCode: string;
  documentRequirementId: string;
  onSuccess: () => void;
}

export function EnableTen99PolicyDialog(props: EnableTen99PolicyDialogProps) {
  const { modalState, workplaceId, suggestedCategoryCode, documentRequirementId, onSuccess } =
    props;

  const [selectedQualifications, setSelectedQualifications] =
    useState<string[]>(DEFAULT_QUALIFICATIONS);

  const {
    data: qualificationsData,
    isLoading: isLoadingQualifications,
    isError: isErrorQualifications,
  } = useGetQualifications();
  const {
    data: workplaceTypesData,
    isLoading: isLoadingWorkplaceTypes,
    isError: isErrorWorkplaceTypes,
  } = useGetWorkplaceTypes();

  const qualificationOptions =
    qualificationsData?.data.map(({ attributes }) => attributes.name) ?? [];
  const workplaceTypeOptions =
    workplaceTypesData?.data.map(({ attributes }) => attributes.name) ?? [];
  const isLoadingDependencies = isLoadingQualifications || isLoadingWorkplaceTypes;

  const { isSubmitting, mappingFailed, mappingErrorMessage, handleSubmit, handleRetryMapping } =
    useEnableTen99PolicyCoverage({
      workplaceId,
      suggestedCategoryCode,
      documentRequirementId,
      qualifications: selectedQualifications,
      workplaceTypes: workplaceTypeOptions,
      modalState,
      onSuccess,
    });

  const categoryCodeDisplayName =
    CATEGORY_CODE_DISPLAY_NAMES[suggestedCategoryCode] ?? suggestedCategoryCode;

  if (mappingFailed) {
    return (
      <MappingFailedDialog
        modalState={modalState}
        isSubmitting={isSubmitting}
        errorMessage={mappingErrorMessage}
        onRetry={() => {
          void handleRetryMapping();
        }}
      />
    );
  }

  return (
    <Dialog fullWidth maxWidth="sm" open={modalState.modalIsOpen} onClose={modalState.closeModal}>
      <DialogTitle>Enable 1099 Policy Coverage</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ marginTop: 1 }}>
          <Box>
            <Text variant="subtitle2" color="text.secondary">
              Category Code
            </Text>
            <Text variant="body1">{categoryCodeDisplayName}</Text>
            <Text variant="caption" color="text.secondary">
              Auto-selected based on the facility&apos;s state
            </Text>
          </Box>

          <Box>
            <Text variant="subtitle2" color="text.secondary">
              Qualifications
            </Text>
            <Autocomplete
              multiple
              options={qualificationOptions}
              loading={isLoadingQualifications}
              value={selectedQualifications}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select qualifications" size="small" />
              )}
              onChange={(_, newValue) => {
                setSelectedQualifications(newValue);
              }}
            />
            <Text variant="caption" color="text.secondary">
              Workers with these qualifications will need coverage to book at this facility
            </Text>
          </Box>

          <Box>
            <Text variant="subtitle2" color="text.secondary">
              What will happen
            </Text>
            <Box component="ul" sx={{ paddingLeft: 2, margin: 0 }}>
              <li>
                <Text variant="body2">A 1099Policy entity will be created for this workplace</Text>
              </li>
              <li>
                <Text variant="body2">
                  A requirement mapping will be created linking the Workers&apos; Compensation
                  Coverage document requirement to this facility
                </Text>
              </li>
              <li>
                <Text variant="body2">
                  Workers will need to complete the 1099Policy coverage application before booking
                </Text>
              </li>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={isSubmitting} onClick={modalState.closeModal}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            isSubmitting ||
            isLoadingDependencies ||
            isErrorQualifications ||
            isErrorWorkplaceTypes ||
            workplaceTypeOptions.length === 0 ||
            selectedQualifications.length === 0
          }
          onClick={() => {
            void handleSubmit();
          }}
        >
          {isSubmitting ? <CircularProgress size={24} /> : "Enable Coverage"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
